"use client";

import React, { createContext, useContext, useEffect, useCallback, useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { setUserLocale } from "@/i18n/locale";
type Language = "en" | "ar";
interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (key: string, values?: Record<string, string | number>) => string;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
export function LanguageProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const currentLocale = useLocale() as Language || "en";
  const tNextIntl = useTranslations();
  const [, startTransition] = useTransition();
  const language = currentLocale;
  useEffect(() => {
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = language;
    localStorage.setItem("app_lang", language);
  }, [language]);
  const toggleLanguage = useCallback(() => {
    const nextLang: Language = language === "en" ? "ar" : "en";
    document.documentElement.dir = nextLang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = nextLang;
    localStorage.setItem("app_lang", nextLang);
    startTransition(() => {
      setUserLocale(nextLang);
    });
  }, [language]);
  const t = useCallback((key: string, values?: Record<string, string | number>): string => {
    try {
      return tNextIntl(key as any, values as any) as string || key;
    } catch {
      return key;
    }
  }, [tNextIntl]);
  return <LanguageContext.Provider value={{
    language,
    toggleLanguage,
    t
  }}>
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}